import React from 'react';
import Link from 'next/link';
import { isSsrPath } from '@sy/ssr-routing';
import { useRouter } from 'next/router';

const SsrLink = ({ children, href, ...props }: Props) => {
	const router = useRouter();
	const isValidSsrPath = isSsrPath(href.toString());

	const getLocalizedHref = (path: string) => {
		// Skip localization for special paths
		if (
			path.startsWith('/_next/') ||
			path.startsWith('/api/') ||
			path.startsWith('/watch/') ||
			path.match(/^\/[a-z]{2}-[a-z]{2}/) // Already localized
		) {
			return path;
		}

		// If we're in a localized context (e.g., /es-es/) and the target path is not already localized
		const localeMatch = router.asPath.match(/^\/([a-z]{2})-([a-z]{2})/);
		if (localeMatch && localeMatch[1] === localeMatch[2]) {
			return path === '/'
				? `/${localeMatch[1]}-${localeMatch[1]}`
				: `/${localeMatch[1]}-${localeMatch[1]}${path}`;
		}

		return path;
	};

	const localizedHref = getLocalizedHref(href.toString());

	if (isValidSsrPath) {
		return (
			<Link href={localizedHref} {...props}>
				{children}
			</Link>
		);
	}

	if (typeof children === 'string') {
		return (
			<a data-testid="regular-link" href={localizedHref} {...props}>
				{children}
			</a>
		);
	}

	return React.cloneElement(children, {
		href: localizedHref,
		...props,
	});
};

type Props = Omit<React.ComponentProps<typeof Link>, 'children'> & {
	children: React.ReactElement | string;
};

export default SsrLink;
